// ============================================
// SYSTEM HEALTH DASHBOARD COMPONENT
// File: frontend/src/pages/SuperAdminSystemHealth.js
// ============================================

import React, { useState, useEffect } from 'react';
import { 
  Activity, Server, Database, Zap, AlertCircle, CheckCircle,
  HardDrive, Cpu, MemoryStick, Globe, RefreshCw, Clock,
  TrendingUp, TrendingDown
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const SuperAdminSystemHealth = () => {
  const { t } = useTranslation();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // ============================================
  // Fetch Health Data
  // ============================================

  const fetchHealth = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch('/api/system-health', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setHealth(data);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error fetching system health:', error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // Auto-refresh Effect
  // ============================================

  useEffect(() => {
    fetchHealth();

    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchHealth, 30000); // 30 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  // ============================================
  // Helper Functions
  // ============================================

  const formatBytes = (bytes) => {
    if (!bytes) return 'N/A';
    const gb = bytes / (1024 ** 3);
    return `${gb.toFixed(2)} GB`;
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${mins}m`;
  };

  const getHealthStatus = () => {
    if (!health) return 'unknown';
    
    // Check critical indicators
    const memoryUsage = parseFloat(health.server.memoryUsagePercent);
    const errorRate = health.errors.last24Hours.errorRate;
    const onlineRate = health.websocket.connectionRate;

    if (memoryUsage > 90 || errorRate > 5 || onlineRate < 50) {
      return 'critical';
    }
    if (memoryUsage > 80 || errorRate > 2 || onlineRate < 70) {
      return 'warning';
    }
    return 'healthy';
  };

  const getStatusBadge = () => {
    const status = getHealthStatus();
    
    switch (status) {
      case 'healthy':
        return (
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 font-medium">
            <CheckCircle className="w-5 h-5" />
            {t('systemHealth.healthy')}
          </span>
        );
      case 'warning':
        return (
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 font-medium">
            <AlertCircle className="w-5 h-5" />
            {t('systemHealth.warning')}
          </span>
        );
      case 'critical':
        return (
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 font-medium">
            <AlertCircle className="w-5 h-5" />
            {t('systemHealth.critical')}
          </span>
        );
      default:
        return null;
    }
  };

  // ============================================
  // Render
  // ============================================

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <Activity className="w-8 h-8 text-green-600 dark:text-green-400" />
              {t('systemHealth.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t('systemHealth.subtitle')}
            </p>
          </div>
          
          <div className="flex gap-3">
            {getStatusBadge()}
            
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                autoRefresh
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? t('systemHealth.autoRefreshOn') : t('systemHealth.autoRefreshOff')}
            </button>

            <button
              onClick={fetchHealth}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              {t('systemHealth.refresh')}
            </button>
          </div>
        </div>

        {lastUpdate && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('systemHealth.lastUpdate')}: {lastUpdate.toLocaleString()}
          </p>
        )}
      </div>

      {loading && !health ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-gray-300 border-t-green-600 rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">{t('common.loading')}</p>
        </div>
      ) : !health ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">{t('systemHealth.error')}</p>
        </div>
      ) : (
        <>
          {/* Key Metrics Row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {/* Response Time */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Zap className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {health.responseTime}ms
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('systemHealth.responseTime')}
                  </p>
                </div>
              </div>
            </div>

            {/* Uptime */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Clock className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {formatUptime(health.server.uptime)}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('systemHealth.uptime')}
                  </p>
                </div>
              </div>
            </div>

            {/* Memory Usage */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <MemoryStick className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {health.server.memoryUsagePercent}%
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('systemHealth.memoryUsage')}
                  </p>
                </div>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${
                    parseFloat(health.server.memoryUsagePercent) > 80 
                      ? 'bg-red-600' 
                      : parseFloat(health.server.memoryUsagePercent) > 60
                        ? 'bg-yellow-600'
                        : 'bg-green-600'
                  }`}
                  style={{ width: `${health.server.memoryUsagePercent}%` }}
                />
              </div>
            </div>

            {/* Error Rate */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {health.errors.last24Hours.errorRate}%
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('systemHealth.errorRate')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Server Metrics */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Server className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                {t('systemHealth.serverMetrics')}
              </h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">{t('systemHealth.hostname')}:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{health.server.hostname}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">{t('systemHealth.platform')}:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {health.server.platform} ({health.server.arch})
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">{t('systemHealth.cpus')}:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{health.server.cpus} cores</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">{t('systemHealth.totalMemory')}:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatBytes(health.server.totalMemory)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">{t('systemHealth.usedMemory')}:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatBytes(health.server.usedMemory)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">{t('systemHealth.freeMemory')}:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatBytes(health.server.freeMemory)}
                  </span>
                </div>

                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{t('systemHealth.loadAverage')}:</p>
                  <div className="flex gap-4">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-500">1 min</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {health.server.loadAverage[0].toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-500">5 min</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {health.server.loadAverage[1].toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-500">15 min</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {health.server.loadAverage[2].toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Disk Usage */}
            {health.disk && (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <HardDrive className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                  {t('systemHealth.diskUsage')}
                </h2>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">{t('systemHealth.filesystem')}:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{health.disk.filesystem}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">{t('systemHealth.size')}:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{health.disk.size}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">{t('systemHealth.used')}:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{health.disk.used}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">{t('systemHealth.available')}:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{health.disk.available}</span>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t('systemHealth.usage')}:</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{health.disk.usePercent}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div 
                        className={`h-3 rounded-full ${
                          parseInt(health.disk.usePercent) > 80 
                            ? 'bg-red-600' 
                            : parseInt(health.disk.usePercent) > 60
                              ? 'bg-yellow-600'
                              : 'bg-green-600'
                        }`}
                        style={{ width: health.disk.usePercent }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Database Metrics */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Database className="w-6 h-6 text-green-600 dark:text-green-400" />
                {t('systemHealth.database')}
              </h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">{t('systemHealth.connections')}:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {health.database.activeConnections}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">{t('systemHealth.dbSize')}:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {health.database.databaseSize}
                  </span>
                </div>

                {health.database.rowCounts && (
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      {t('systemHealth.rowCounts')}:
                    </p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {Object.entries(health.database.rowCounts).map(([table, count]) => (
                        <div key={table} className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">{table}:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {count.toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* WebSocket Status */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Globe className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
                {t('systemHealth.websocket')}
              </h2>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">{t('systemHealth.totalScreens')}:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {health.websocket.totalScreens}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">{t('systemHealth.onlineScreens')}:</span>
                  <span className="font-medium text-green-600 dark:text-green-400 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    {health.websocket.onlineScreens}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">{t('systemHealth.offlineScreens')}:</span>
                  <span className="font-medium text-red-600 dark:text-red-400 flex items-center gap-2">
                    <TrendingDown className="w-4 h-4" />
                    {health.websocket.offlineScreens}
                  </span>
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {t('systemHealth.connectionRate')}:
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {health.websocket.connectionRate}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full ${
                        parseFloat(health.websocket.connectionRate) < 50 
                          ? 'bg-red-600' 
                          : parseFloat(health.websocket.connectionRate) < 80
                            ? 'bg-yellow-600'
                            : 'bg-green-600'
                      }`}
                      style={{ width: `${health.websocket.connectionRate}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Errors */}
          {health.errors.recentErrors && health.errors.recentErrors.length > 0 && (
            <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                {t('systemHealth.recentErrors')}
              </h2>
              
              <div className="space-y-3">
                {health.errors.recentErrors.map((error, index) => (
                  <div key={index} className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-red-900 dark:text-red-300">
                            {error.action_type} - {error.resource_type}
                          </span>
                          <span className="text-xs text-red-700 dark:text-red-400">
                            {new Date(error.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-red-800 dark:text-red-400">
                          {error.error_message}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SuperAdminSystemHealth;